/**
 * Browser-hosted assistant proposal from a natural-language goal.
 * Mirrors desktop `assistantGoalProposal.ts` but uses Owlbert cloud `llm.chatAuto` instead of IPC.
 */

export function buildAssistantProposalPrompt(userGoal: string): string {
  const trimmed = userGoal.trim()
  return `You are helping design a custom AI assistant for an Owlbert workspace user.

The user described what they want this assistant to do:
"""
${trimmed}
"""

Reply with ONLY a single JSON object (UTF-8 plain text, no markdown code fences, no commentary) using exactly these keys:
"name","description","instructions","greeting"

Constraints:
- "name": short display name, max 50 characters.
- "description": one or two sentences for a UI card (max 280 characters).
- "instructions": a complete system prompt the assistant will follow: role, tone, tasks, boundaries, how to handle uncertainty, and output preferences tailored to the user's goal.
- "greeting": one friendly first message (one or two short sentences) the assistant could send when a chat begins.

Valid JSON only. Escape any double quotes inside string values.`
}

export type AssistantProposalFields = {
  name: string
  description: string
  instructions: string
  greeting: string
}

export function parseAssistantProposalJson(raw: string): AssistantProposalFields {
  let s = raw.trim()
  const fence = /^```(?:json)?\s*\n?([\s\S]*?)\n?```$/im
  const fenceMatch = s.match(fence)
  const inner = fenceMatch?.[1]
  if (inner) s = inner.trim()
  const start = s.indexOf('{')
  const end = s.lastIndexOf('}')
  if (start < 0 || end <= start) {
    throw new Error('Model response did not contain JSON.')
  }
  s = s.slice(start, end + 1)
  const obj = JSON.parse(s) as Record<string, unknown>
  const name = typeof obj.name === 'string' ? obj.name.trim() : ''
  const description = typeof obj.description === 'string' ? obj.description.trim() : ''
  const instructions = typeof obj.instructions === 'string' ? obj.instructions.trim() : ''
  const greeting = typeof obj.greeting === 'string' ? obj.greeting.trim() : ''
  if (!name || !instructions) {
    throw new Error('Model JSON was missing required fields (name, instructions).')
  }
  return {
    name: name.slice(0, 80),
    description: description.slice(0, 400),
    instructions,
    greeting,
  }
}

export type WebLlmChatAutoLike = {
  chatAuto: {
    mutate: (input: {
      messages?: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>
      system_prompt?: string
      temperature?: number
    }) => Promise<{ content?: string | null }>
  }
}

/**
 * Uses Owlbert cloud default LLM (`chatAuto`) to propose name, description, instructions, and greeting.
 */
export async function generateAssistantProposalFromDescription(
  llm: WebLlmChatAutoLike,
  userGoal: string,
): Promise<AssistantProposalFields> {
  const goal = userGoal.trim()
  if (!goal) {
    throw new Error('Describe what you want the assistant to help with.')
  }
  const reply = await llm.chatAuto.mutate({
    messages: [{ role: 'user', content: buildAssistantProposalPrompt(goal) }],
    system_prompt:
      'You follow instructions exactly. Reply with ONLY a single JSON object as requested in the user message — no markdown code fences, no commentary before or after.',
    temperature: 0.25,
  })
  const content = reply.content ?? ''
  return parseAssistantProposalJson(content)
}
